"use client";

import { Link } from "@/navigation";
import { useTranslations } from "next-intl";
import { CloudArrowUp } from "@phosphor-icons/react";
import type { Icon } from "@phosphor-icons/react";
import { useModalInquiryContext } from "@/context/ModalInquiryContext";

export interface MaterialCtaProps {
	// 翻译命名空间
	translationNamespace?: string;
	// 自定义文案（优先级高于翻译）
	text?: string;
	// 自定义按钮文字（优先级高于翻译）
	buttonText?: string;
	// 按钮跳转链接（buttonType 为 "link" 时生效）
	buttonHref?: string;
	// 翻译键（用于从命名空间获取文案）
	textKey?: string;
	buttonTextKey?: string;
	// 自定义图标组件
	icon?: Icon;
	// 图标大小
	iconSize?: number;
	// 是否显示图标
	showIcon?: boolean;
	// 自定义容器类名
	className?: string;
	// 按钮类型：link 跳转 | modal 弹窗，默认跳转
	buttonType?: "link" | "modal";
	// 文案最大宽度
	textMaxWidth?: string;
	// 按钮文字最大宽度
	buttonTextMaxWidth?: string;
}

export default function MaterialCta({
	translationNamespace,
	text,
	buttonText,
	buttonHref = "/upload",
	textKey = "ctaText",
	buttonTextKey = "ctaButton",
	icon: IconComponent = CloudArrowUp,
	iconSize = 20,
	showIcon = true,
	className = "",
	buttonType = "link",
	textMaxWidth,
	buttonTextMaxWidth,
}: MaterialCtaProps) {
	const t = translationNamespace ? useTranslations(translationNamespace) : null;
	const { openModalInquiry } = useModalInquiryContext();

	// 获取显示文案
	const displayText = text || (t ? t(textKey) : "");
	const displayButtonText = buttonText || (t ? t(buttonTextKey) : "Get Quote");
	const textStyle = textMaxWidth ? { flexBasis: textMaxWidth, maxWidth: textMaxWidth } : undefined;
	const buttonTextStyle = buttonTextMaxWidth ? { maxWidth: buttonTextMaxWidth } : undefined;

	// 按钮样式
	const buttonClassName =
		"inline-flex min-h-[58px] min-w-[220px] items-center justify-center gap-3 rounded-md bg-themePrimary400 px-6 py-3 text-center text-lg font-extrabold leading-tight text-white transition-colors hover:bg-mainColor hover:text-white sm:text-xl";

	return (
		<div
			className={`mx-auto my-6 flex w-full max-w-[780px] flex-col items-center justify-between gap-4 rounded-md border border-[#858585] bg-white px-5 py-4 shadow-[0_2px_5px_rgba(0,0,0,0.28)] sm:my-8 sm:min-h-[92px] sm:flex-row sm:gap-8 sm:px-8 ${className}`}
		>
			{displayText && (
				<p
					className="min-w-0 flex-1 text-left text-lg font-extrabold leading-tight text-black sm:text-xl md:text-[22px]"
					style={textStyle}
				>
					{displayText}
				</p>
			)}
			{buttonType === "link" ? (
				buttonHref.startsWith("http") ? (
					<a href={buttonHref} target="_blank" rel="noopener noreferrer" className={buttonClassName}>
						<span className="inline-block text-center" style={buttonTextStyle}>
							{displayButtonText}
						</span>
						{showIcon && <IconComponent size={iconSize} weight="bold" />}
					</a>
				) : (
					<Link href={buttonHref} className={buttonClassName}>
						<span className="inline-block text-center" style={buttonTextStyle}>
							{displayButtonText}
						</span>
						{showIcon && <IconComponent size={iconSize} weight="bold" />}
					</Link>
				)
			) : (
				<button onClick={openModalInquiry} className={buttonClassName}>
					<span className="inline-block text-center" style={buttonTextStyle}>
						{displayButtonText}
					</span>
					{showIcon && <IconComponent size={iconSize} weight="bold" />}
				</button>
			)}
		</div>
	);
}
